import type { DisplayPrice } from '@platform/types';
import { Check, MessageSquare, Phone, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/currency';

export interface PlanCardProps {
  name: string;
  description?: string | null;
  priceCents: number;
  currency: string;
  /** Approximate converted price in the viewer's display currency. The plan
   *  is still billed in `currency` — this only adds a "≈ …" hint line. */
  displayPrice?: DisplayPrice | null;
  /** Inbound voice minutes / period — primary voice quota. */
  voiceMinutesIncluded: number;
  /** Chat messages / period — primary chat quota. */
  chatMessagesIncluded: number;
  /** Capability line (not metered), e.g. concurrency. */
  concurrency?: number;
  /** Plan includes caller transfer (Plan.callTransferEnabled). */
  callTransferEnabled?: boolean;
  isTrial: boolean;
  trialDays?: number | null;
  /** Channels bundled in this tier. When set, renders Voice/Chat badges under
   *  the price. Every paid tier ships both — this is the platform's moat. */
  channels?: Array<'voice' | 'chat'>;
  highlighted?: boolean;
  badge?: string;
  features?: string[];
  ctaLabel: string;
  ctaDisabled?: boolean;
  onCta: () => void;
  footer?: ReactNode;
}

/**
 * Pricing card. Used on both the marketing pricing page and the dashboard
 * upgrade flow. The shape matches Plan DTO from @platform/types — keep this
 * a pure presentational component so the same data renders both contexts.
 */
export function PlanCard(props: PlanCardProps): JSX.Element {
  const price = formatMoney(props.priceCents, props.currency);
  return (
    <div
      className={cn(
        'relative flex flex-col rounded-xl border bg-card p-6 shadow-sm transition-shadow',
        props.highlighted && 'border-primary shadow-md ring-2 ring-primary/20',
      )}
    >
      {props.badge ? (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
          <Sparkles className="-mt-0.5 mr-1 inline h-3 w-3" />
          {props.badge}
        </span>
      ) : null}

      <h3 className="text-lg font-semibold">{props.name}</h3>
      {props.description ? (
        <p className="mt-1 text-sm text-muted-foreground">{props.description}</p>
      ) : null}

      <div className="mt-4">
        {props.isTrial ? (
          <p className="text-3xl font-bold">Free</p>
        ) : (
          <>
            <p className="text-3xl font-bold">
              {price}
              <span className="ml-1 text-base font-normal text-muted-foreground">
                / month
              </span>
            </p>
            {props.displayPrice ? (
              <p className="mt-1 text-sm text-muted-foreground">
                ≈ {formatMoney(props.displayPrice.amountCents, props.displayPrice.currency)}{' '}
                / month · billed in {props.currency.toUpperCase()}
              </p>
            ) : null}
          </>
        )}
        <p className="mt-1 text-sm text-muted-foreground">{quotaLine(props)}</p>
        {props.channels && props.channels.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {props.channels.includes('voice') ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                <Phone className="h-3 w-3" />
                Voice
              </span>
            ) : null}
            {props.channels.includes('chat') ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <MessageSquare className="h-3 w-3" />
                Chat
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <ul className="mt-6 space-y-2 text-sm">
        {(props.features ?? defaultFeatures(props)).map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-6">
        <Button
          className="w-full"
          variant={props.highlighted ? 'default' : 'outline'}
          disabled={props.ctaDisabled}
          onClick={props.onCta}
        >
          {props.ctaLabel}
        </Button>
        {props.footer ? (
          <div className="mt-2 text-center text-xs text-muted-foreground">
            {props.footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Headline quota line — minute-based pricing (voice minutes + chat messages). */
function quotaLine(props: PlanCardProps): string {
  const period = props.isTrial ? `over ${props.trialDays ?? 14} days` : '/ month';
  return `${props.voiceMinutesIncluded} inbound minutes · ${props.chatMessagesIncluded} chat messages ${period}`;
}

function defaultFeatures(props: PlanCardProps): string[] {
  const features = [
    `${props.voiceMinutesIncluded} inbound voice minutes + ${props.chatMessagesIncluded} chat messages${props.isTrial ? ' (trial)' : ' / month'}`,
    '24/7/365 — never miss a call or message',
    ...(props.concurrency ? [`${props.concurrency} concurrent calls`] : []),
    'One knowledge base across both channels',
    'Transcripts + AI summaries',
    ...(props.callTransferEnabled ? ['Caller transfer rules'] : []),
  ];
  if (!props.isTrial) {
    features.push('Usage analytics + monthly invoice');
  }
  return features;
}
